import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// Static image list — same content in preview & production, no DB required.
// To add/remove images, edit this array and redeploy.
const IMAGES = [
  {
    url: "https://customer-assets-eiarnc6j.emergentagent.net/job_wowshe-preview/artifacts/5mg3urgc_WhatsApp%20Image%202026-07-31%20at%2010.38.02%20AM.jpeg",
    caption: "Meet Our HEAD TABLE · WOWSHE Founding Circle Launch",
  },
  {
    url: "https://customer-assets-eiarnc6j.emergentagent.net/job_wowshe-preview/artifacts/f04fg35g_WhatsApp%20Image%202026-07-31%20at%2010.38.03%20AM.jpeg",
    caption: "Guest of Honour · Shailaja Kota, M Santi Kumari, P. Sabitha",
  },
  {
    url: "https://customer-assets-eiarnc6j.emergentagent.net/job_wowshe-preview/artifacts/hw815zcx_WhatsApp%20Image%202026-07-31%20at%2010.38.02%20AM%20%281%29.jpeg",
    caption: "A gift exchange · sisterhood in action",
  },
  {
    url: "https://customer-assets-eiarnc6j.emergentagent.net/job_wowshe-preview/artifacts/yrg47vpr_WhatsApp%20Image%202026-07-31%20at%2010.38.01%20AM.jpeg",
    caption: "Team meet at Telangana Minority Department",
  },
  {
    url: "https://customer-assets-eiarnc6j.emergentagent.net/job_wowshe-preview/artifacts/034tj6qk_WhatsApp%20Image%202026-07-31%20at%2010.38.03%20AM%20%281%29.jpeg",
    caption: "Three founders · one shared vision",
  },
  {
    url: "https://customer-assets-eiarnc6j.emergentagent.net/job_wowshe-preview/artifacts/ojvenqa4_WhatsApp%20Image%202026-07-31%20at%2010.38.04%20AM.jpeg",
    caption: "A warm moment · flowers for a founder",
  },
];

export default function Gallery() {
  const images = IMAGES;
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  const close = useCallback(() => setLightboxIdx(-1), []);
  const prev = useCallback(() => setLightboxIdx(i => images.length ? (i - 1 + images.length) % images.length : i), [images]);
  const next = useCallback(() => setLightboxIdx(i => images.length ? (i + 1) % images.length : i), [images]);

  useEffect(() => {
    if (lightboxIdx < 0) return;
    const handler = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [lightboxIdx, close, prev, next]);

  return (
    <div data-testid="gallery-page">
      {/* HERO */}
      <section className="section-pad bg-gradient-to-br from-lavender/70 via-white to-champagne/[0.14] relative overflow-hidden">
        <div aria-hidden className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-rose/[0.10] blur-3xl pointer-events-none" />
        <div aria-hidden className="absolute -bottom-24 -left-24 w-[440px] h-[440px] rounded-full bg-champagne/[0.14] blur-3xl pointer-events-none" />
        <div className="container-luxe relative text-center max-w-3xl mx-auto">
          <p className="overline text-rose">The WOWSHE Story · In pictures</p>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-aubergine leading-[1.05] mt-4">Gallery</h1>
          <p className="mt-5 text-plumSoft text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
            Moments that celebrate leadership, collaboration, learning, and the WOWSHE community.
          </p>
        </div>
      </section>

      {/* GRID */}
      <section className="container-luxe py-14 md:py-16">
        {images.length === 0 ? (
          <div className="text-center py-16">
            <p className="font-display text-2xl text-aubergine">No photos yet</p>
            <p className="mt-2 text-plumSoft">New moments will land here after every event.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {images.map((img, i) => (
              <button
                key={i}
                data-testid={`gallery-img-${i}`}
                onClick={() => setLightboxIdx(i)}
                className="group relative rounded-2xl overflow-hidden border border-aubergine/[0.08] bg-lavender/40 focus:outline-none focus:ring-2 focus:ring-aubergine/40 aspect-[4/5]"
              >
                <img
                  src={img.url}
                  alt={img.caption}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-[1.04] transition-transform duration-500"
                />
                {img.caption && (
                  <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-aubergine/85 via-aubergine/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-[12px] text-ivory leading-snug text-left">{img.caption}</p>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* LIGHTBOX */}
      {lightboxIdx >= 0 && images[lightboxIdx] && (
        <div className="fixed inset-0 z-[100] bg-aubergine/95 backdrop-blur-sm flex items-center justify-center p-4" data-testid="gallery-lightbox">
          <button
            onClick={close}
            aria-label="Close"
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-ivory/10 hover:bg-ivory/20 text-ivory flex items-center justify-center transition"
          >
            <X className="w-5 h-5" />
          </button>
          {images.length > 1 && (
            <>
              <button
                onClick={prev}
                aria-label="Previous"
                className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-ivory/10 hover:bg-ivory/20 text-ivory flex items-center justify-center transition"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                aria-label="Next"
                className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-ivory/10 hover:bg-ivory/20 text-ivory flex items-center justify-center transition"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
          <div className="max-w-[92vw] max-h-[85vh] flex flex-col items-center gap-4">
            <img src={images[lightboxIdx].url} alt={images[lightboxIdx].caption || ""} className="max-w-full max-h-[75vh] rounded-xl object-contain" />
            <div className="text-ivory text-center max-w-2xl">
              {images[lightboxIdx].caption && <p className="text-sm md:text-base leading-relaxed">{images[lightboxIdx].caption}</p>}
              <p className="mt-1 text-[10px] tracking-[0.28em] uppercase text-ivory/60">
                {lightboxIdx + 1} / {images.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
